// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import type {JOB_INFORMATION} from 'src/types/crm.js';

import {
  BodyMedium,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Table} from '@spaced-out/ui-design-system/lib/components/Table';
// $FlowFixMe[untyped-import]
import {formatMomentDateAndTime} from 'src/utils/date-time';
import {CardTitle} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  ButtonTabs,
  ButtonTab,
} from '@spaced-out/ui-design-system/lib/components/ButtonTabs';

import {converJobTypeString, getExperience, getRangeLabel} from 'src/utils/crm';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {hasCrmPermission, CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import {BasicJobDetailsTab as BasicDetailsTab} from 'src/components/crm/lib/job-details/basic-job-details.jsx';
import {SkillsTab} from 'src/components/crm/lib/job-details/skills-tab.jsx';
import {AdditionalInfoTab} from 'src/components/crm/lib/job-details/additional-job-info.jsx';
import {CustomJobDetails} from 'src/components/crm/lib/job-details/custom-job-details.jsx';
import {useInflight} from 'src/hooks/redux';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import css from 'src/components/crm/lib/job-details-tab/job-details-tab.css';
import {useSelector, useDispatch} from 'react-redux';
import {selectJobInformation} from 'src/selectors/crm';
import {getJobInformation} from 'src/action-creators/crm';


export const JobDetailsTab = ({
  jobDetails,
  hideCustomInfo,
}: {
  jobDetails: JOB_INFORMATION,
  hideCustomInfo?: boolean,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [selectedInfoType, setSelectedInfoType] = React.useState('basic');

  const TAB_ITEMS = [
    {
      label: t('Basic_Info', 'Basic Info'),
      type: 'basic',
    },
    {
      label: t('Skills', 'Skills'),
      type: 'skills',
    },
    ...(!hideCustomInfo
      ? [
          {
            label: t('Custom_Info', 'Custom Info'),
            type: 'custom',
          },
        ]
      : []),
    {
      label: t('Additional_Info', 'Additional Info'),
      type: 'additionalInfo',
    },
  ];

  const TabItems = () => (
    <ButtonTabs
      size="small"
      onButtonTabSelect={(type) => setSelectedInfoType(type)}
      selectedButtonTabId={selectedInfoType}
      classNames={{wrapper: css.buttonTabs}}
    >
      {TAB_ITEMS.map(({label, type}) => (
        <ButtonTab id={type} key={type}>
          {label}
        </ButtonTab>
      ))}
    </ButtonTabs>
  );

  return (
    <div className={css.basicInfoContainer}>
      <div>
        <CardTitle>
          <TabItems TAB_ITEMS={TAB_ITEMS} type={selectedInfoType} />
        </CardTitle>
      </div>

      <div className={css.infoContainer}>
        {selectedInfoType === 'basic' && (
          <>
            <div className={css.content}>
              <BasicDetailsTab jobId={jobDetails.jobId.toString()} />
            </div>
          </>
        )}

        {selectedInfoType === 'skills' && (
          <>
            <SkillsTab jobId={jobDetails.jobId.toString()} />
          </>
        )}

        {!hideCustomInfo && selectedInfoType === 'custom' && (
          <CustomJobDetails jobId={jobDetails.jobId.toString()} />
        )}

        {selectedInfoType === 'additionalInfo' && (
          <>
            <AdditionalInfoTab jobId={jobDetails.jobId.toString()} />
          </>
        )}
      </div>
    </div>
  );
};
